import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Link,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Alert,
  Toolbar,
} from '@mui/material';
import { Visibility, VisibilityOff, ErrorOutline } from '@mui/icons-material';
import { Info } from '@mui/icons-material';
import { Send } from '@mui/icons-material';
import { showError, toast } from '../utils/errorHandler';
import { LoginForm, RegistrationForm } from '../components/forms';
import {
  DEFAULT_PAID_PLAN,
  OnboardingUserData,
  PlanType,
  isPaidPlanType,
  normalizePaidPlanType,
  normalizePlanType,
  onboardingService,
} from '../modules/onboarding/services/onboardingService';
import EmbeddedFreeOnboarding from '../modules/onboarding/free/components/EmbeddedFreeOnboarding';
import EmbeddedPaidOnboarding from '../modules/onboarding/paid/components/EmbeddedPaidOnboarding';
import { useAuth } from '../hooks/useAuth';
import { DESIGN_TOKENS } from '../design-system/tokens/colors';
import { Header } from '../components/layout/Header';
import { asaasService } from '../services/asaasService';
import { apiClientBff } from '../services/apiBff';
import { Loading } from '../components/ui/Loading';

const backgroundImageUrl = '/login/3842beb8df5520fd4674019652e302f1ff5b9cb0.png';
const iconEmail = '/login/email.svg';
const iconLock = '/login/security.svg';
const iconCheck = '/login/chek.svg';
const iconData = '/login/SVG-1.svg';
const iconMoney = '/login/SVG-2.svg';

export const AuthPage: React.FC = () => {
  const { t } = useTranslation(['forms', 'landing']);
  const { login, authState } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [planFromUrl, setPlanFromUrl] = useState<PlanType | null>(null);
  const [onboardingUser, setOnboardingUser] = useState<OnboardingUserData | null>(null);
  const [blockAuthRedirect, setBlockAuthRedirect] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [hasAcceptedPrivacy, setHasAcceptedPrivacy] = useState(false);

  const fullText = t('common:auth_page.typing_text', 'Qual foi a última vacina da vaca #127?');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const plan = normalizePlanType(urlParams.get('plan'));
    const signUpParam = urlParams.get('signup');
    
    setPlanFromUrl(plan);
    if (signUpParam && (signUpParam === 'true' || signUpParam === '1')) {
      setIsSignUp(true);
    }

    const existingUserData = onboardingService.getOnboardingUserData();
    if (existingUserData) {
      setOnboardingUser(existingUserData);
    }
  }, []);

  useEffect(() => {
    if (!authState.isAuthenticated || isLoading || blockAuthRedirect) return;
    let cancelled = false;

    const redirectAfterLogin = async () => {
      if (authState.hasActivePlan) {
        navigate('/dashboard', { replace: true });
        return;
      }

      const email = String(authState.user?.email || '').trim().toLowerCase();
      if (email) {
        try {
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('timeout')), 10000);
          });
          
          const access = await Promise.race([
            asaasService.checkAccessByEmail(email),
            timeoutPromise
          ]) as any;
          
          if (cancelled) return;
          
          const accessGranted = Boolean(access?.accessGranted);
          const hasPendingSubscription = Boolean(access?.subscription) && !accessGranted;
          
          if (!accessGranted) {
            if (cancelled) return;
            navigate('/subscription-required', { replace: true });
            return;
          }
          
          if (cancelled) return;
          navigate('/dashboard', { replace: true });
        } catch (error) {
          console.error('Error checking subscription access:', error);
          if (cancelled) return;
          navigate('/dashboard', { replace: true });
        }
      } else {
        if (cancelled) return;
        navigate('/subscription-required', { replace: true });
      }
    };

    redirectAfterLogin();

    return () => {
      cancelled = true;
    };
  }, [authState.isAuthenticated, authState.hasActivePlan, isLoading, blockAuthRedirect, navigate]);

  useEffect(() => {
    if (!isTyping && fullText) {
      const timer = setTimeout(() => {
        setIsTyping(true);
        let currentIndex = 0;
        const typingInterval = setInterval(() => {
          if (currentIndex <= fullText.length) {
            setDisplayText(fullText.slice(0, currentIndex));
            currentIndex++;
          } else {
            clearInterval(typingInterval);
            setTimeout(() => {
              setDisplayText('');
              setIsTyping(false);
            }, 10000);
          }
        }, 50);
        return () => clearInterval(typingInterval);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isTyping, fullText]);


  const generateUsername = (emailStr: string): string => {
    if (!emailStr) return `user_${Math.floor(Math.random() * 10000)}`;
    const localPart = emailStr.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');
    const randomSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `${localPart}_${randomSuffix}`.substring(0, 30);
  };

  const handleLogin = async (emailForm: string, passwordForm: string) => {
    setIsLoading(true);
    setSuccessMessage('');

    try {
      const normalizedEmail = emailForm.toLowerCase().trim();
      const result = await login({
        email: normalizedEmail,
        password: passwordForm,
        rememberMe,
      });

      if (!result.success) {
        if (result.message?.includes('plano') || result.message?.includes('assinatura')) {
          setBlockAuthRedirect(true);
          navigate('/subscription-required', { replace: true });
        } else {
          toast.error(result.message || 'Erro ao fazer login.');
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);
      showError(error);
    } finally {
      setIsLoading(false);
    }
  };


  const handleRegister = async () => {
    setIsLoading(true);
    setSuccessMessage('');

    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem.');
      setIsLoading(false);
      return;
    }

    if (!hasAcceptedTerms || !hasAcceptedPrivacy) {
      toast.error('Você precisa aceitar os Termos de uso e a Política de privacidade.');
      setIsLoading(false);
      return;
    }

    try {
      const extractJwtToken = (input: any): string | null => {
        const candidate =
          input?.token ??
          input?.accessToken ??
          input?.access_token ??
          input?.data?.token ??
          input?.data?.accessToken ??
          input?.data?.access_token;
        return typeof candidate === 'string' && candidate.trim().startsWith('eyJ') ? candidate.trim() : null;
      };

      const payload = {
        email: email.trim().toLowerCase(),
        password: password,
        username: generateUsername(email),
        roles: ["user"],
      };

      const response = await apiClientBff.authPost<any>("/auth/register", payload);

      const isSuccess = Boolean(
        response?.token || response?.user || (typeof response?.message === 'string' && response.message.toLowerCase().includes('success'))
      );

      if (isSuccess) {
        const emailToPass = email.trim().toLowerCase();
        let registrationToken = extractJwtToken(response);
        if (!registrationToken) {
          const loginResponse = await apiClientBff.authPost<any>('/auth/login', {
            email: emailToPass,
            password,
          });
          registrationToken = extractJwtToken(loginResponse);
          if (!registrationToken) {
            throw new Error('Conta criada, mas nao foi possivel autenticar automaticamente. Faça login para continuar.');
          }
        }

        onboardingService.storeOnboardingTrialBootstrapState({
          email: emailToPass,
          shouldCreate: true,
          token: registrationToken,
        });
        
        navigate('/plan-selection', { 
          state: { 
            fromAuth: true,
            registrationJustCreated: true,
            registrationToken,
            registrationEmail: emailToPass,
            registrationPassword: password,
            email: emailToPass,
            password: password,
            successMessage: 'Conta criada com sucesso! Selecione um plano para continuar.'
          } 
        });
      } else {
        const msg = response?.error || response?.message || 'Erro ao criar conta.';
        throw new Error(msg);
      }
    } catch (error: any) {
      console.error('Register error:', error);
      showError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSignUp) {
      handleRegister();
    } else {
      handleLogin(email, password);
    }
  };

  const handleRegisterRedirect = () => {
    setIsSignUp(true);
    navigate('/auth?signup=true', { replace: true });
  };

  const handleLoginRedirect = () => {
    setIsSignUp(false);
    navigate('/auth', { replace: true });
  };

  return (
    <>
      <Header progress={0} />
      <Toolbar />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#f7faf9',
          p: { xs: 2, sm: 3, md: 4 },
          mx: { xs: 0, sm: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.1),0px 4px 6px -4px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            maxWidth: 1440,
            width: '100%',
            minHeight: { xs: 'auto', md: '921.59px' },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Box
            sx={{
              flex: 1,
              backgroundColor: '#003d4d',
              minHeight: { xs: 400, md: '100%' },
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={backgroundImageUrl}
              alt=""
              sx={{
                position: 'absolute',
                width: 1891,
                height: 2103,
                left: -1136,
                top: -1075.2,
                objectFit: 'cover',
                pointerEvents: 'none',
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: `url('data:image/svg+xml;utf8,<svg viewBox="0 0 620 921.59" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none"><g transform="matrix(-16.55 -54.23 54.23 -16.55 475.5 542.3)" opacity="0.699999988079071"><rect height="99.358" width="175.78" fill="url(%23grad)" id="quad" shape-rendering="crispEdges"/><use href="%23quad" transform="scale(1 -1)"/><use href="%23quad" transform="scale(-1 1)"/><use href="%23quad" transform="scale(-1 -1)"/></g><defs><linearGradient id="grad" gradientUnits="userSpaceOnUse" x2="5" y2="5"><stop stop-color="rgba(15,23,42,0.1)" offset="0"/><stop stop-color="rgba(15,23,42,1)" offset="0.81838"/></linearGradient></defs></svg>')`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                top: 0,
                width: { xs: '100%', md: 513 },
                p: 6,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"New Order", sans-serif',
                  fontWeight: 700,
                  fontSize: '2rem',
                  color: '#ffffff',
                  pb: 4.5,
                }}
              >
                MUU Agrotech
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  mt: 6,
                  maxWidth: 576,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontWeight: 600,
                    fontSize: '2.5rem',
                    color: '#ffffff',
                    lineHeight: 1.5,
                  }}
                >
                  {t('landing:auth_page_new.title_line1')}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontWeight: 600,
                    fontSize: '2.5rem',
                    color: '#ffffff',
                    lineHeight: 1.5,
                  }}
                >
                  {t('landing:auth_page_new.title_line2')}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontWeight: 400,
                    fontSize: '1.25rem',
                    color: '#ffffff',
                    lineHeight: 1.4,
                    mt: 1,
                  }}
                >
                  {t('landing:auth_page_new.description')}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  mt: 'auto',
                  pt: 8,
                }}
              >
                {(t('landing:auth_page_new.features', { returnObjects: true }) as string[]).map((feature, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    <Box
                      component="img"
                      src={index === 0 ? iconCheck : index === 1 ? iconData : iconMoney}
                      alt=""
                      sx={{ width: 24, height: 24 }}
                    />
                    <Typography
                      sx={{
                        fontFamily: '"New Order", sans-serif',
                        fontWeight: 400,
                        fontSize: '1.125rem',
                        color: '#ffffff',
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              backgroundColor: '#f7faf9',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 8,
              minHeight: { xs: 'auto', md: '100%' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                maxWidth: 448,
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontWeight: 600,
                    fontSize: '1.5rem',
                    color: '#333b3e',
                  }}
                >
                  {isSignUp ? t('landing:auth_page_new.register.title') : t('landing:auth_page_new.login.title')}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    color: '#41484b',
                  }}
                >
                  {isSignUp ? t('landing:auth_page_new.register.subtitle') : t('landing:auth_page_new.login.subtitle')}
                </Typography>
              </Box>

              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: '"New Order", sans-serif',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: '#41484b',
                      letterSpacing: '0.24px',
                    }}
                  >
                    {isSignUp ? t('landing:auth_page_new.register.email') : t('landing:auth_page_new.login.email')}
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    placeholder="nome@email.com.br"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="img"
                            src={iconEmail}
                            alt=""
                            sx={{ width: 20, height: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f1f4f3',
                        borderRadius: '8px',
                        height: 57,
                        '& fieldset': {
                          borderColor: '#c0c8cc',
                        },
                        '&:hover fieldset': {
                          borderColor: '#004e64',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#004e64',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontFamily: '"New Order", sans-serif',
                        fontSize: '1rem',
                        color: '#6b7280',
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"New Order", sans-serif',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        color: '#41484b',
                        letterSpacing: '0.24px',
                      }}
                    >
                      {isSignUp ? t('landing:auth_page_new.register.password') : t('landing:auth_page_new.login.password')}
                    </Typography>
                    {!isSignUp && (
                      <Typography
                        component={RouterLink}
                        to="/auth/forgot-password"
                        sx={{
                          fontFamily: '"New Order", sans-serif',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          color: '#004e64',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {t('landing:auth_page_new.login.forgot_password')}
                      </Typography>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="img"
                            src={iconLock}
                            alt=""
                            sx={{ width: 20, height: 20 }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? (
                              <VisibilityOff sx={{ fontSize: 20, color: '#6b7280' }} />
                            ) : (
                              <Visibility sx={{ fontSize: 20, color: '#6b7280' }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f1f4f3',
                        borderRadius: '8px',
                        '& fieldset': {
                          borderColor: '#c0c8cc',
                        },
                        '&:hover fieldset': {
                          borderColor: '#004e64',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#004e64',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '1rem',
                        color: '#6b7280',
                      },
                    }}
                  />
                </Box>

                {isSignUp && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: '"New Order", sans-serif',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        color: '#41484b',
                        letterSpacing: '0.24px',
                      }}
                    >
                      {t('landing:auth_page_new.register.confirm_password')}
                    </Typography>
                    <TextField
                      fullWidth
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box
                              component="img"
                              src={iconLock}
                              alt=""
                              sx={{ width: 20, height: 20 }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              size="small"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff sx={{ fontSize: 20, color: '#6b7280' }} />
                              ) : (
                                <Visibility sx={{ fontSize: 20, color: '#6b7280' }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#f1f4f3',
                          borderRadius: '8px',
                          '& fieldset': {
                            borderColor: '#c0c8cc',
                          },
                          '&:hover fieldset': {
                            borderColor: '#004e64',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#004e64',
                          },
                        },
                        '& .MuiInputBase-input': {
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '1rem',
                          color: '#6b7280',
                        },
                      }}
                    />
                  </Box>
                )}

                {isSignUp ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={hasAcceptedTerms}
                          onChange={(e) => setHasAcceptedTerms(e.target.checked)}
                          sx={{
                            color: '#c0c8cc',
                            '&.Mui-checked': {
                              color: '#004e64',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontFamily: '"New Order", sans-serif',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            color: '#41484b',
                          }}
                        >
                          {t('landing:auth_page_new.register.terms_label')}{' '}
                          <Link component={RouterLink} to="/terms" sx={{ color: '#004e64' }}>{t('landing:auth_page_new.register.terms_link')}</Link>
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={hasAcceptedPrivacy}
                          onChange={(e) => setHasAcceptedPrivacy(e.target.checked)}
                          sx={{
                            color: '#c0c8cc',
                            '&.Mui-checked': {
                              color: '#004e64',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontFamily: '"New Order", sans-serif',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            color: '#41484b',
                          }}
                        >
                          {t('landing:auth_page_new.register.privacy_label')}{' '}
                          <Link component={RouterLink} to="/privacy" sx={{ color: '#004e64' }}>{t('landing:auth_page_new.register.privacy_link')}</Link>
                        </Typography>
                      }
                    />
                  </Box>
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        sx={{
                          color: '#c0c8cc',
                          '&.Mui-checked': {
                            color: '#004e64',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: '"New Order", sans-serif',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          color: '#41484b',
                        }}
                      >
                        {t('landing:auth_page_new.login.remember_me')}
                      </Typography>
                    }
                  />
                )}

                <Box
                  component="button"
                  type="submit"
                  disabled={isLoading || (isSignUp && (!hasAcceptedTerms || !hasAcceptedPrivacy))}
                  sx={{
                    backgroundColor: '#004e64',
                    boxShadow: '0px 1px 1px rgba(0,0,0,0.05)',
                    borderRadius: '8px',
                    py: 2,
                    px: 0,
                    width: '100%',
                    border: 'none',
                    cursor: (isLoading || (isSignUp && (!hasAcceptedTerms || !hasAcceptedPrivacy))) ? 'not-allowed' : 'pointer',
                    opacity: (isLoading || (isSignUp && (!hasAcceptedTerms || !hasAcceptedPrivacy))) ? 0.7 : 1,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: (isLoading || (isSignUp && (!hasAcceptedTerms || !hasAcceptedPrivacy))) ? '#004e64' : '#003a4d',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"New Order", sans-serif',
                      fontWeight: 600,
                      fontSize: '1.125rem',
                      color: '#ffffff',
                      textAlign: 'center',
                    }}
                  >
                    {isLoading ? (isSignUp ? t('landing:auth_page_new.register.submit') + '...' : t('landing:auth_page_new.login.submit') + '...') : (isSignUp ? t('landing:auth_page_new.register.submit') : t('landing:auth_page_new.login.submit'))}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ py: 4, position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    borderTop: '1px solid #c0c8cc',
                  }}
                />
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    backgroundColor: '#f7faf9',
                    px: 2,
                    mx: 'auto',
                    width: 'fit-content',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"New Order", sans-serif',
                      fontWeight: 400,
                      fontSize: '0.875rem',
                      color: '#41484b',
                    }}
                  >
                    {isSignUp ? t('landing:auth_page_new.register.has_account') : t('landing:auth_page_new.login.no_account')}{' '}
                    <Box
                      component="span"
                      onClick={isSignUp ? handleLoginRedirect : handleRegisterRedirect}
                      sx={{
                        color: '#004e64',
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {isSignUp ? t('landing:auth_page_new.register.signin') : t('landing:auth_page_new.login.signup')}
                    </Box>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};
